/**
 * Did the window we rendered lose data off its FRONT?
 *
 * THE FAILURE THIS CATCHES. A server computes the bounds of a project's telemetry
 * and returns a slice. If that computation starts late — admin-cosmos measured a
 * lifetime bound beginning 06:11:13Z against a true first telemetry of 05:23:04Z —
 * the client renders a chart that looks complete. Row counts are healthy, the axes
 * fit, nothing errors. The first 48 minutes are simply not there, and no reader
 * can tell.
 *
 * IT IS THE SAME SHAPE AS THE MISSING-SI BUG, one step subtler. A missing SI
 * produced NaN and something threw; a late bound produces a number that is
 * finite, positive, ordered and wrong. Every guard written against malformed input
 * passes it.
 *
 * WHY THIS IS THE CLIENT'S JOB AND NOT ONLY THE SERVER'S. Trusting a
 * server-supplied bound is a CLASS of exposure, not one defect: any endpoint that
 * decides a range on our behalf can front-truncate, and the fix for one bound does
 * not protect the next. The client is the only place that sees several payloads
 * for the same project and can notice they disagree.
 *
 * THE REFERENCE IS THE EARLIEST TICK EVER SEEN for that project, from any payload
 * this session — because the client has no independent source of truth for when a
 * cure began. That makes this a CONSISTENCY check rather than a correctness one,
 * and it is honest about what it can prove:
 *
 *   - it CANNOT tell that a first-ever payload is truncated. Nothing has
 *     contradicted it yet.
 *   - it CAN tell the moment any later payload — a different endpoint, a refetch,
 *     a wider zoom — reaches further back than a window that claimed to be whole.
 *
 * That is exactly the 48-minute case: loss and temperature cover one project, and
 * a bound that truncates one of them disagrees with the other.
 */

/** How far apart two "starts" may be before it is a truncation, not rounding. */
export const WINDOW_START_TOLERANCE_MS = 60_000;

export interface WindowStartCheck {
  /** The project the payload belongs to. Windows are only comparable within one. */
  project: string;
  /** Which payload this was, for a message that names the disagreeing pair. */
  source: string;
  /** Epoch ms of the first row actually rendered. */
  renderedFirstTick: number;
  /**
   * Did this request ask for the WHOLE lifetime? A deliberately narrowed window —
   * a user zoom — starts late by design and must never be reported.
   */
  claimsFullLifetime: boolean;
}

export interface WindowStartVerdict {
  kind: 'ok' | 'first-observation' | 'front-truncated' | 'ignored';
  /** Present when truncated: how much was lost off the front. */
  missingMs?: number;
  message?: string;
}

/**
 * @i18n-exempt — every string here is a console diagnostic, not user-visible
 * copy. Translating them would help nobody and would break the browser gate,
 * which matches on the English text.
 */

/**
 * The earliest tick seen per project, and the payload that produced it.
 *
 * Deliberately a plain map with no expiry: a session is short, and forgetting the
 * reference is how a truncation stops being detectable.
 */
const earliest = new Map<string, { tick: number; source: string }>();

/** Forget everything — between projects in a test, or on sign-out. */
export function resetWindowIntegrity(): void {
  earliest.clear();
}

/**
 * Record a payload and report whether it front-truncated.
 *
 * Records EVEN WHEN IT REPORTS, so the earliest known tick only ever moves
 * backwards. A truncated payload must not become the reference that makes the
 * next, correct payload look wrong.
 */
export function checkWindowStart(check: WindowStartCheck): WindowStartVerdict {
  const { project, source, renderedFirstTick, claimsFullLifetime } = check;

  if (!project || !Number.isFinite(renderedFirstTick)) {
    return { kind: 'ignored' };
  }
  // A zoomed window starts late because the user asked it to.
  if (!claimsFullLifetime) {
    return { kind: 'ignored' };
  }

  const known = earliest.get(project);
  if (!known) {
    earliest.set(project, { tick: renderedFirstTick, source });
    return { kind: 'first-observation' };
  }

  if (renderedFirstTick < known.tick) {
    // This payload reaches further back: it becomes the reference, and the
    // PREVIOUS one was the truncated view.
    const missingMs = known.tick - renderedFirstTick;
    earliest.set(project, { tick: renderedFirstTick, source });
    if (missingMs > WINDOW_START_TOLERANCE_MS) {
      return {
        kind: 'front-truncated',
        missingMs,
        message:
          `WINDOW FRONT-TRUNCATED: "${known.source}" for ${project} started ` +
          `${new Date(known.tick).toISOString()}, but "${source}" reaches back to ` +
          `${new Date(renderedFirstTick).toISOString()} — ${Math.round(missingMs / 60000)} ` +
          `minute(s) were missing from the earlier window.`,
      };
    }
    return { kind: 'ok' };
  }

  const missingMs = renderedFirstTick - known.tick;
  if (missingMs > WINDOW_START_TOLERANCE_MS) {
    return {
      kind: 'front-truncated',
      missingMs,
      message:
        `WINDOW FRONT-TRUNCATED: "${source}" for ${project} starts ` +
        `${new Date(renderedFirstTick).toISOString()}, but "${known.source}" already ` +
        `showed data from ${new Date(known.tick).toISOString()} — ` +
        `${Math.round(missingMs / 60000)} minute(s) are missing from this window.`,
    };
  }
  return { kind: 'ok' };
}

/**
 * Report a verdict to the console.
 *
 * console.ERROR, not warn, and that is the point of the whole module: the browser
 * gate fails a page with console errors, so a front-truncated window stops being
 * something a person has to notice in a chart and becomes something CI catches.
 */
export function reportWindowStart(verdict: WindowStartVerdict): void {
  if (verdict.kind === 'front-truncated' && verdict.message) {
    console.error(verdict.message);
  }
}
